import React, { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import {
  Upload,
  MapPin,
  Calendar,
  Phone,
  Mail,
  AlertTriangle,
  Camera,
  DollarSign,
  FileText,
  Save,
  ArrowLeft,
} from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { functions, storage } from "../firebase";
import { useNavigate } from "react-router-dom";
import MapPicker from "../components/MapPicker";

const RegisterLostDogPage = ({ user }) => {
  const { themeClasses } = useTheme();
  const navigate = useNavigate();
  const [userPets, setUserPets] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Pet Selection
    existingPetId: "",
    useExistingPet: true,

    // Basic Pet Info (if not using existing pet)
    petName: "",
    breed: "",
    age: "",
    gender: "",
    color: "",
    size: "",
    microchipNumber: "",

    // Lost Dog Specific Info
    dateLost: "",
    timeLost: "",
    lastSeenLocation: "",
    lastSeenAddress: "",
    circumstances: "",

    // Contact Information
    contactPhone: "",
    contactEmail: "",
    alternatePhone: "",

    // Additional Details
    description: "",
    behaviorNotes: "",
    medicalConditions: "",
    specialInstructions: "",

    // Reward & Social
    reward: "",
    rewardAmount: "",
    shareOnSocial: true,

    // Photo
    photoFile: null,
    photoURL: "",

    // Geolocation
    locationData: null,
  });

  useEffect(() => {
    if (user) {
      loadUserProfile();
      loadUserPets();
    }
  }, [user]);

  const loadUserProfile = async () => {
    try {
      console.log("Loading user profile...");
      const getUserProfile = httpsCallable(functions, "getUserProfile");
      const result = await getUserProfile();
      console.log("getUserProfile raw result:", result);

      if (result.data.success && result.data.data) {
        const profile = result.data.data;
        setUserProfile(profile);

        // Format address from object to string
        const addressString = profile.address
          ? [
              profile.address.street,
              profile.address.city,
              profile.address.state,
              profile.address.zipCode,
            ]
              .filter(Boolean)
              .join(", ")
          : "";

        // Auto-populate contact information from user profile
        setFormData((prev) => ({
          ...prev,
          contactEmail: user.email || "",
          contactPhone: profile?.phoneNumber || "",
          alternatePhone: profile?.emergencyContact?.phoneNumber || "",
          lastSeenLocation: addressString || "",
          lastSeenAddress: addressString || "",
        }));

        console.log("User profile loaded and contact info populated:", {
          phone: profile?.phoneNumber,
          emergencyPhone: profile?.emergencyContact?.phoneNumber,
          address: addressString,
        });
      } else {
        console.log("No user profile found, using basic user info");
        // Fallback to basic user info
        setFormData((prev) => ({
          ...prev,
          contactEmail: user.email || "",
        }));
      }
    } catch (error) {
      console.error("Error loading user profile:", error);
      // Fallback to basic user info
      setFormData((prev) => ({
        ...prev,
        contactEmail: user.email || "",
      }));
    }
  };

  const loadUserPets = async () => {
    try {
      console.log("Loading user pets...");
      const getUserPets = httpsCallable(functions, "getUserPets");
      const result = await getUserPets();

      console.log("getUserPets result:", result.data);

      if (result.data.success) {
        console.log("Setting user pets:", result.data.pets);
        setUserPets(result.data.pets);
      } else {
        console.log("getUserPets returned unsuccessful result:", result.data);
      }
    } catch (error) {
      console.error("Error loading pets:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Update user profile when contact information changes
    if (["contactPhone", "alternatePhone", "lastSeenAddress"].includes(field)) {
      updateUserContactInfo(field, value);
    }
  };

  const updateUserContactInfo = async (field, value) => {
    try {
      const updateUserProfile = httpsCallable(functions, "updateUserProfile");
      const updates = {};

      if (field === "contactPhone") {
        updates.phoneNumber = value;
      } else if (field === "alternatePhone") {
        // Update the entire emergencyContact object, preserving existing fields
        const currentEmergencyContact = userProfile?.emergencyContact || {};
        updates.emergencyContact = {
          ...currentEmergencyContact,
          phoneNumber: value,
        };
      } else if (field === "lastSeenAddress") {
        // Parse the address string back to components
        const parts = value.split(", ");
        updates.address = {
          street: parts[0] || "",
          city: parts[1] || "",
          state: parts[2] || "",
          zipCode: parts[3] || "",
          country: "US", // Default
        };
      }

      if (Object.keys(updates).length > 0) {
        await updateUserProfile({ updates });
        console.log(`User profile updated: ${field} = ${value}`);

        // Update local userProfile state
        setUserProfile((prev) => {
          const updated = { ...(prev || {}) };

          // Apply updates directly (no more dot notation handling needed)
          Object.keys(updates).forEach((key) => {
            updated[key] = updates[key];
          });

          return updated;
        });
      }
    } catch (error) {
      console.error("Error updating user profile:", error);
      // Don't show error to user as this is a background operation
    }
  };

  const updateUserProfileFromForm = async () => {
    try {
      const updateUserProfile = httpsCallable(functions, "updateUserProfile");
      const updates = {};

      // Check if contact info has changed from original profile
      if (userProfile) {
        const currentAddress = userProfile.address
          ? [
              userProfile.address.street,
              userProfile.address.city,
              userProfile.address.state,
              userProfile.address.zipCode,
            ]
              .filter(Boolean)
              .join(", ")
          : "";

        if (formData.contactPhone !== (userProfile.phoneNumber || "")) {
          updates.phoneNumber = formData.contactPhone;
        }
        if (
          formData.alternatePhone !==
          (userProfile.emergencyContact?.phoneNumber || "")
        ) {
          const currentEmergencyContact = userProfile?.emergencyContact || {};
          updates.emergencyContact = {
            ...currentEmergencyContact,
            phoneNumber: formData.alternatePhone,
          };
        }
        if (formData.lastSeenAddress !== currentAddress) {
          const parts = formData.lastSeenAddress.split(", ");
          updates.address = {
            street: parts[0] || "",
            city: parts[1] || "",
            state: parts[2] || "",
            zipCode: parts[3] || "",
            country: "US",
          };
        }
      } else {
        // If no profile loaded, save all contact info
        if (formData.contactPhone) updates.phoneNumber = formData.contactPhone;
        if (formData.alternatePhone) {
          updates.emergencyContact = {
            phoneNumber: formData.alternatePhone,
          };
        }
        if (formData.lastSeenAddress) {
          const parts = formData.lastSeenAddress.split(", ");
          updates.address = {
            street: parts[0] || "",
            city: parts[1] || "",
            state: parts[2] || "",
            zipCode: parts[3] || "",
            country: "US",
          };
        }
      }

      if (Object.keys(updates).length > 0) {
        await updateUserProfile({ updates });
        console.log("User profile updated from form:", updates);
      }
    } catch (error) {
      console.error("Error updating user profile from form:", error);
      // Don't throw error as this shouldn't block the lost dog registration
    }
  };

  const handlePetSelection = (petId) => {
    const selectedPet = userPets.find((pet) => pet.id === petId);
    if (selectedPet) {
      setFormData((prev) => ({
        ...prev,
        // Pet Information
        existingPetId: petId,
        petName: selectedPet.name,
        breed: selectedPet.breed,
        age: selectedPet.age,
        gender: selectedPet.gender,
        color: selectedPet.color,
        size: selectedPet.size,
        microchipNumber: selectedPet.microchipNumber || "",
        photoURL: selectedPet.avatarThumbnail || selectedPet.photos?.[0] || "",
        // Keep existing contact information - don't overwrite it
        // contactPhone, contactEmail, alternatePhone, lastSeenLocation, lastSeenAddress remain unchanged
      }));
      console.log(
        "Pet selected:",
        selectedPet.name,
        "- Contact info preserved"
      );
    }
  };

  const handleLocationSelect = (locationData) => {
    setFormData((prev) => ({
      ...prev,
      locationData: locationData,
      // Update text fields if address is available
      lastSeenLocation: locationData?.address || prev.lastSeenLocation,
      lastSeenAddress: locationData?.address || prev.lastSeenAddress,
    }));
    console.log("Location selected:", locationData);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        photoFile: file,
      }));
    }
  };

  const uploadPhoto = async () => {
    if (!formData.photoFile) return formData.photoURL;

    try {
      const photoRef = ref(
        storage,
        `lost-dogs/${Date.now()}-${formData.photoFile.name}`
      );
      await uploadBytes(photoRef, formData.photoFile);
      return await getDownloadURL(photoRef);
    } catch (error) {
      console.error("Error uploading photo:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log("Starting lost dog registration...");
      console.log("Form data:", formData);

      // Upload photo if provided
      let photoURL = formData.photoURL;
      if (formData.photoFile) {
        console.log("Uploading new photo file...");
        photoURL = await uploadPhoto();
        console.log("Photo uploaded successfully:", photoURL);
      } else if (formData.photoURL) {
        console.log("Using existing photo URL:", photoURL);
      } else {
        console.log("No photo provided");
      }

      // Prepare lost dog data
      const lostDogData = {
        // Owner Information
        ownerId: user.uid,
        ownerName: user.displayName || user.email,
        ownerEmail: user.email,

        // Pet Information
        petId: formData.existingPetId || null,
        petName: formData.petName,
        breed: formData.breed,
        age: formData.age,
        gender: formData.gender,
        color: formData.color,
        size: formData.size,
        microchipNumber: formData.microchipNumber,
        photoURL,

        // Lost Details
        dateLost: formData.dateLost,
        timeLost: formData.timeLost,
        lastSeenLocation: formData.lastSeenLocation,
        lastSeenAddress: formData.lastSeenAddress,
        circumstances: formData.circumstances,

        // Location Data (coordinates)
        locationData: formData.locationData,

        // Contact Information
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
        alternatePhone: formData.alternatePhone,

        // Additional Information
        description: formData.description,
        behaviorNotes: formData.behaviorNotes,
        medicalConditions: formData.medicalConditions,
        specialInstructions: formData.specialInstructions,

        // Reward Information
        reward: formData.reward,
        rewardAmount: formData.rewardAmount,

        // Additional fields
        shareOnSocial: formData.shareOnSocial,
      };

      console.log("Prepared lost dog data:", lostDogData);

      // Update user profile with any contact changes before registering
      console.log("Updating user profile...");
      await updateUserProfileFromForm();
      console.log("User profile updated successfully");

      // Use Firebase Function to register lost dog
      console.log("Calling registerLostDog function...");
      const registerLostDog = httpsCallable(functions, "registerLostDog");
      const result = await registerLostDog(lostDogData);
      console.log("registerLostDog result:", result);

      if (result.data.success) {
        console.log("Lost dog registered successfully!");
        alert(
          `${formData.petName} has been registered as lost. ID: ${result.data.lostDog.id}`
        );
        navigate("/dashboard/lost-dogs");
      } else {
        console.error("Registration failed:", result.data);
        alert("Registration failed: " + (result.data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error registering lost dog:", error);

      // Extract more specific error message
      let errorMessage = "Error registering lost dog. Please try again.";
      if (error.message) {
        errorMessage = error.message;
      } else if (error.details) {
        errorMessage = error.details;
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
      >
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate("/dashboard/lost-dogs")}
            className={`p-2 rounded-lg border ${themeClasses.border.primary} ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors`}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className={`${themeClasses.text.primary} text-3xl font-bold`}>
              Register Lost Dog
            </h1>
            <p className={`${themeClasses.text.secondary} text-lg`}>
              Help us help you find your missing companion
            </p>
          </div>
        </div>

        {/* Emergency Alert */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-red-800 dark:text-red-200">
              <p className="font-medium mb-1">Time is critical!</p>
              <p className="text-sm">
                The first 24-48 hours are crucial for finding lost pets.
                Complete this form quickly and we'll help spread the word
                immediately.
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Pet Selection */}
        {userPets.length > 0 && (
          <div
            className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
          >
            <h2
              className={`${themeClasses.text.primary} text-xl font-bold mb-4`}
            >
              Select Your Pet
            </h2>

            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={formData.useExistingPet}
                    onChange={() => handleInputChange("useExistingPet", true)}
                    className="text-blue-600"
                  />
                  <span className={themeClasses.text.primary}>
                    Use existing pet
                  </span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={!formData.useExistingPet}
                    onChange={() => handleInputChange("useExistingPet", false)}
                    className="text-blue-600"
                  />
                  <span className={themeClasses.text.primary}>
                    Enter pet details manually
                  </span>
                </label>
              </div>

              {formData.useExistingPet && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {userPets.map((pet) => (
                    <div
                      key={pet.id}
                      onClick={() => handlePetSelection(pet.id)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        formData.existingPetId === pet.id
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                          : `${themeClasses.border.primary} hover:border-blue-300`
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                          {pet.avatarThumbnail || pet.photos?.[0] ? (
                            <img
                              src={pet.avatarThumbnail || pet.photos?.[0]}
                              alt={pet.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-lg">
                              🐕
                            </div>
                          )}
                        </div>
                        <div>
                          <div
                            className={`${themeClasses.text.primary} font-medium`}
                          >
                            {pet.name}
                          </div>
                          <div
                            className={`${themeClasses.text.secondary} text-sm`}
                          >
                            {pet.breed} • {pet.gender}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pet Information */}
        {(!formData.useExistingPet || userPets.length === 0) && (
          <div
            className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
          >
            <h2
              className={`${themeClasses.text.primary} text-xl font-bold mb-4`}
            >
              Pet Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                >
                  Pet Name *
                </label>
                <input
                  type="text"
                  value={formData.petName}
                  onChange={(e) => handleInputChange("petName", e.target.value)}
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                  required
                />
              </div>

              <div>
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                >
                  Breed *
                </label>
                <input
                  type="text"
                  value={formData.breed}
                  onChange={(e) => handleInputChange("breed", e.target.value)}
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                  required
                />
              </div>

              <div>
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                >
                  Age
                </label>
                <input
                  type="text"
                  value={formData.age}
                  onChange={(e) => handleInputChange("age", e.target.value)}
                  placeholder="e.g., 3 years, 8 months"
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                />
              </div>

              <div>
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                >
                  Gender
                </label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleInputChange("gender", e.target.value)}
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              <div>
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                >
                  Color/Markings
                </label>
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => handleInputChange("color", e.target.value)}
                  placeholder="e.g., Brown with white chest"
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                />
              </div>

              <div>
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                >
                  Size
                </label>
                <select
                  value={formData.size}
                  onChange={(e) => handleInputChange("size", e.target.value)}
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                >
                  <option value="">Select Size</option>
                  <option value="Small">Small (under 25 lbs)</option>
                  <option value="Medium">Medium (25-60 lbs)</option>
                  <option value="Large">Large (60-90 lbs)</option>
                  <option value="Extra Large">Extra Large (over 90 lbs)</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                >
                  Microchip Number
                </label>
                <input
                  type="text"
                  value={formData.microchipNumber}
                  onChange={(e) =>
                    handleInputChange("microchipNumber", e.target.value)
                  }
                  placeholder="15-digit microchip number (if known)"
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                />
              </div>
            </div>
          </div>
        )}

        {/* Lost Details */}
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
        >
          <h2
            className={`${themeClasses.text.primary} text-xl font-bold mb-4 flex items-center space-x-2`}
          >
            <Calendar className="w-5 h-5" />
            <span>When & Where</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
              >
                Date Lost *
              </label>
              <input
                type="date"
                value={formData.dateLost}
                onChange={(e) => handleInputChange("dateLost", e.target.value)}
                max={new Date().toISOString().split("T")[0]}
                className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                required
              />
            </div>

            <div>
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
              >
                Approximate Time
              </label>
              <input
                type="time"
                value={formData.timeLost}
                onChange={(e) => handleInputChange("timeLost", e.target.value)}
                className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
              />
            </div>

            <div>
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
              >
                Last Seen Location *
              </label>
              <input
                type="text"
                value={formData.lastSeenLocation}
                onChange={(e) =>
                  handleInputChange("lastSeenLocation", e.target.value)
                }
                placeholder="e.g., Central Park, Main Street"
                className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                required
              />
              {userProfile?.address && (
                <p className={`${themeClasses.text.secondary} text-xs mt-1`}>
                  💡 Pre-filled from your profile address. Update if different.
                </p>
              )}
            </div>

            <div>
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
              >
                Full Address (if known)
              </label>
              <input
                type="text"
                value={formData.lastSeenAddress}
                onChange={(e) =>
                  handleInputChange("lastSeenAddress", e.target.value)
                }
                placeholder="Street address, city, state"
                className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
              />
              <p className={`${themeClasses.text.secondary} text-xs mt-1`}>
                Changes will update your profile address automatically
              </p>
            </div>

            {/* Map Picker */}
            <div className="md:col-span-2">
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-2`}
              >
                <MapPin className="w-4 h-4 inline mr-1" />
                Precise Location (Optional)
              </label>
              <p className={`${themeClasses.text.secondary} text-sm mb-3`}>
                Select the exact location on the map where your pet was last
                seen. This helps with search coordination and nearby alerts.
              </p>
              <MapPicker
                onLocationSelect={handleLocationSelect}
                initialLocation={formData.locationData}
                disabled={loading}
              />
            </div>

            <div className="md:col-span-2">
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
              >
                How did they get lost? *
              </label>
              <textarea
                value={formData.circumstances}
                onChange={(e) =>
                  handleInputChange("circumstances", e.target.value)
                }
                placeholder="Describe the circumstances: escaped from yard, slipped collar during walk, etc."
                className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                rows="3"
                required
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
        >
          <h2
            className={`${themeClasses.text.primary} text-xl font-bold mb-4 flex items-center space-x-2`}
          >
            <Phone className="w-5 h-5" />
            <span>Contact Information</span>
          </h2>

          {userProfile && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
              <p className={`${themeClasses.text.secondary} text-sm`}>
                ✅ Contact information loaded from your profile. Changes will be
                saved automatically.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
              >
                Primary Phone *
              </label>
              <input
                type="tel"
                value={formData.contactPhone}
                onChange={(e) =>
                  handleInputChange("contactPhone", e.target.value)
                }
                placeholder="(555) 123-4567"
                className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                required
              />
              <p className={`${themeClasses.text.secondary} text-xs mt-1`}>
                Required - Must be at least 8 digits for emergency contact
              </p>
            </div>

            <div>
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
              >
                Email Address
              </label>
              <input
                type="email"
                value={formData.contactEmail}
                onChange={(e) =>
                  handleInputChange("contactEmail", e.target.value)
                }
                className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
              />
            </div>

            <div>
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
              >
                Alternate Phone
              </label>
              <input
                type="tel"
                value={formData.alternatePhone}
                onChange={(e) =>
                  handleInputChange("alternatePhone", e.target.value)
                }
                placeholder="Family member or friend"
                className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
              />
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
        >
          <h2
            className={`${themeClasses.text.primary} text-xl font-bold mb-4 flex items-center space-x-2`}
          >
            <FileText className="w-5 h-5" />
            <span>Additional Details</span>
          </h2>

          <div className="space-y-4">
            <div>
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
              >
                Physical Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Distinguishing features, scars, collar details, etc."
                className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                rows="3"
              />
            </div>

            <div>
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
              >
                Behavior & Temperament
              </label>
              <textarea
                value={formData.behaviorNotes}
                onChange={(e) =>
                  handleInputChange("behaviorNotes", e.target.value)
                }
                placeholder="Friendly, shy, aggressive, responds to name, etc."
                className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                rows="2"
              />
            </div>

            <div>
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
              >
                Medical Conditions
              </label>
              <textarea
                value={formData.medicalConditions}
                onChange={(e) =>
                  handleInputChange("medicalConditions", e.target.value)
                }
                placeholder="Any medications, health issues, or special needs"
                className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                rows="2"
              />
            </div>

            <div>
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
              >
                Special Instructions
              </label>
              <textarea
                value={formData.specialInstructions}
                onChange={(e) =>
                  handleInputChange("specialInstructions", e.target.value)
                }
                placeholder="How to approach, what NOT to do, etc."
                className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                rows="2"
              />
            </div>
          </div>
        </div>

        {/* Photo Upload */}
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
        >
          <h2
            className={`${themeClasses.text.primary} text-xl font-bold mb-4 flex items-center space-x-2`}
          >
            <Camera className="w-5 h-5" />
            <span>Recent Photo</span>
          </h2>

          <div className="space-y-4">
            <div>
              <label
                className={`block ${themeClasses.text.primary} text-sm font-medium mb-2`}
              >
                Upload the most recent photo of your pet
              </label>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                />
                <label htmlFor="photo-upload" className="cursor-pointer">
                  <Upload
                    className={`mx-auto w-12 h-12 ${themeClasses.text.secondary} mb-4`}
                  />
                  <p
                    className={`${themeClasses.text.primary} font-medium mb-1`}
                  >
                    Click to upload photo
                  </p>
                  <p className={`${themeClasses.text.secondary} text-sm`}>
                    PNG, JPG, GIF up to 10MB
                  </p>
                </label>
              </div>

              {(formData.photoFile || formData.photoURL) && (
                <div className="mt-4">
                  <img
                    src={
                      formData.photoFile
                        ? URL.createObjectURL(formData.photoFile)
                        : formData.photoURL
                    }
                    alt="Pet preview"
                    className="w-32 h-32 object-cover rounded-lg border"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Reward Information */}
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
        >
          <h2
            className={`${themeClasses.text.primary} text-xl font-bold mb-4 flex items-center space-x-2`}
          >
            <DollarSign className="w-5 h-5" />
            <span>Reward (Optional)</span>
          </h2>

          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={!!formData.reward}
                  onChange={(e) =>
                    handleInputChange("reward", e.target.checked ? "Yes" : "")
                  }
                  className="text-blue-600"
                />
                <span className={themeClasses.text.primary}>
                  Offering a reward
                </span>
              </label>
            </div>

            {formData.reward && (
              <div>
                <label
                  className={`block ${themeClasses.text.primary} text-sm font-medium mb-1`}
                >
                  Reward Amount
                </label>
                <input
                  type="text"
                  value={formData.rewardAmount}
                  onChange={(e) =>
                    handleInputChange("rewardAmount", e.target.value)
                  }
                  placeholder="e.g., $500, No questions asked"
                  className={`w-full px-3 py-2 border ${themeClasses.border.primary} rounded-lg ${themeClasses.bg.input} ${themeClasses.text.primary}`}
                />
              </div>
            )}
          </div>
        </div>

        {/* Social Sharing */}
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
        >
          <h2 className={`${themeClasses.text.primary} text-xl font-bold mb-4`}>
            Social Media Sharing
          </h2>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.shareOnSocial}
              onChange={(e) =>
                handleInputChange("shareOnSocial", e.target.checked)
              }
              className="text-blue-600"
            />
            <label className={themeClasses.text.primary}>
              Allow sharing on social media platforms to increase visibility
            </label>
          </div>
        </div>

        {/* Submit Button */}
        <div className="space-y-4">
          {/* Validation Helper */}
          {(!formData.petName ||
            !formData.dateLost ||
            !formData.lastSeenLocation ||
            !formData.contactPhone) && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className={`${themeClasses.text.primary} font-medium mb-2`}>
                Please complete the following required fields:
              </p>
              <ul
                className={`${themeClasses.text.secondary} text-sm space-y-1`}
              >
                {!formData.petName && <li>• Pet Name</li>}
                {!formData.dateLost && <li>• Date Lost</li>}
                {!formData.lastSeenLocation && <li>• Last Seen Location</li>}
                {!formData.contactPhone && <li>• Primary Phone Number</li>}
              </ul>
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/dashboard/lost-dogs")}
              className={`px-6 py-3 rounded-lg border ${themeClasses.border.primary} ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                !formData.petName ||
                !formData.dateLost ||
                !formData.lastSeenLocation ||
                !formData.contactPhone
              }
              className={`px-6 py-3 rounded-lg ${themeClasses.bg.button} text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2`}
              onClick={() => {
                // Debug validation when button is clicked
                console.log("Form validation check:", {
                  loading,
                  petName: formData.petName,
                  dateLost: formData.dateLost,
                  lastSeenLocation: formData.lastSeenLocation,
                  contactPhone: formData.contactPhone,
                  isDisabled:
                    loading ||
                    !formData.petName ||
                    !formData.dateLost ||
                    !formData.lastSeenLocation ||
                    !formData.contactPhone,
                });
              }}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Registering...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Register Lost Dog</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RegisterLostDogPage;
